import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Home, BookOpen, CheckCircle2, Library, CalendarDays, LogOut, Upload, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/learner", label: "Accueil", icon: Home, end: true, tid: "learner-nav-accueil-link" },
  { to: "/learner/seances", label: "Séances", icon: BookOpen, tid: "learner-nav-seances-link" },
  { to: "/learner/missions", label: "Missions", icon: CheckCircle2, tid: "learner-nav-missions-link" },
  { to: "/learner/messagerie", label: "Messages", icon: MessageCircle, tid: "learner-nav-messagerie-link", badge: true },
  { to: "/learner/travaux", label: "Travaux", icon: Upload, tid: "learner-nav-travaux-link" },
  { to: "/learner/ressources", label: "Ressources", icon: Library, tid: "learner-nav-ressources-link" },
  { to: "/learner/calendrier", label: "Calendrier", icon: CalendarDays, tid: "learner-nav-calendrier-link" },
];

export default function LearnerLayout() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("dec_learner_token");
    if (!token) {
      navigate("/", { replace: true });
      return;
    }
    api.get("/learner/me")
      .then(({ data }) => setMe(data))
      .catch(() => { localStorage.removeItem("dec_learner_token"); navigate("/", { replace: true }); })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    const poll = () => api.get("/learner/messages/unread_count").then(({ data }) => setUnread(data.count)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [me]);

  if (loading || !me) return <div className="min-h-screen flex items-center justify-center text-[#5C6561]">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 lg:pb-0">
      <header className="bg-white border-b border-[#E6E1DA] sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D95D39] text-white flex items-center justify-center font-bold text-sm">D.</div>
            <div>
              <div className="font-serif-accent text-base leading-none">Décomplexé<span className="text-[#D95D39]">.e</span></div>
              <div className="text-[11px] text-[#5C6561] mt-0.5">Bonjour {me.first_name} 👋</div>
            </div>
          </div>
          <Button
            data-testid="learner-logout-button"
            variant="ghost" size="sm" className="text-[#5C6561]"
            onClick={() => { localStorage.removeItem("dec_learner_token"); navigate("/"); }}
          >
            <LogOut className="w-4 h-4 mr-1" /> Quitter
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-5 py-6">
        <Outlet context={{ me }} />
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E6E1DA] lg:hidden z-30">
        <div className="grid grid-cols-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.tid + "-mobile"}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${
                  isActive ? "text-[#D95D39]" : "text-[#5C6561]"
                }`
              }
            >
              <l.icon className="w-5 h-5" />
              {l.badge && unread > 0 && (
                <span className="absolute top-1 right-[22%] min-w-4 h-4 px-1 rounded-full bg-[#D95D39] text-white text-[9px] font-semibold flex items-center justify-center">{unread}</span>
              )}
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <nav className="hidden lg:flex fixed left-6 top-24 flex-col gap-1 dec-card p-3 w-52">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.tid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
                isActive ? "bg-[#FDF0EC] text-[#D95D39]" : "text-[#1F2421] hover:bg-[#F4EFEA]"
              }`
            }
          >
            <l.icon className="w-4 h-4" />
            {l.label}
            {l.badge && unread > 0 && (
              <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#D95D39] text-white text-[11px] font-semibold flex items-center justify-center" data-testid="learner-unread-badge">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
